<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  ref,
  useTemplateRef,
  watch,
  type Component,
} from "vue";
import { isNothing, toMaybe, type Maybe } from "../lib/maybe";
import type { FormatCommand } from "../editor/port";
import { EDITOR_FONTS, fontStackFor, type EditorFontId } from "../editor/fonts";
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconBulletList,
  IconComment,
  IconHeading1,
  IconHeading2,
  IconHeading3,
  IconHighlight,
  IconImage,
  IconIndent,
  IconItalic,
  IconLink,
  IconNumberedList,
  IconOutdent,
  IconQuote,
  IconRedo,
  IconStrikethrough,
  IconUnderline,
  IconUndo,
} from "./ribbonIcons";

const props = withDefaults(
  defineProps<{
    modLabel: string;
    altLabel: string;
    bodyFontId: EditorFontId;
    headingFontId: EditorFontId;
    /** Unused in project mode; fonts live in project settings. */
    showFonts?: boolean;
  }>(),
  { showFonts: false },
);

const emit = defineEmits<{
  command: [command: FormatCommand];
  "update:bodyFontId": [fontId: EditorFontId];
  "update:headingFontId": [fontId: EditorFontId];
}>();

const showFontControls = computed(() => props.showFonts);

type RibbonItem =
  | {
      type: "button";
      command: FormatCommand;
      icon: Component;
      title: string;
    }
  | { type: "divider" };

const items = computed<RibbonItem[]>(() => [
  {
    type: "button",
    command: "undo",
    icon: IconUndo,
    title: `Undo (${props.modLabel}Z)`,
  },
  {
    type: "button",
    command: "redo",
    icon: IconRedo,
    title: `Redo (${props.modLabel}⇧Z)`,
  },
  { type: "divider" },
  {
    type: "button",
    command: "bold",
    icon: IconBold,
    title: `Bold (${props.modLabel}B)`,
  },
  {
    type: "button",
    command: "italic",
    icon: IconItalic,
    title: `Italic (${props.modLabel}I)`,
  },
  {
    type: "button",
    command: "underline",
    icon: IconUnderline,
    title: `Underline (${props.modLabel}U)`,
  },
  {
    type: "button",
    command: "strikethrough",
    icon: IconStrikethrough,
    title: `Strikethrough (${props.modLabel}⇧X)`,
  },
  {
    type: "button",
    command: "highlight",
    icon: IconHighlight,
    title: `Highlight (${props.modLabel}⇧H)`,
  },
  { type: "divider" },
  {
    type: "button",
    command: "bulletList",
    icon: IconBulletList,
    title: `Bullet list (${props.modLabel}⇧8)`,
  },
  {
    type: "button",
    command: "numberedList",
    icon: IconNumberedList,
    title: `Numbered list (${props.modLabel}⇧7)`,
  },
  {
    type: "button",
    command: "quote",
    icon: IconQuote,
    title: `Quote (${props.modLabel}⇧.)`,
  },
  {
    type: "button",
    command: "outdent",
    icon: IconOutdent,
    title: `Outdent (${props.modLabel}[)`,
  },
  {
    type: "button",
    command: "indent",
    icon: IconIndent,
    title: `Indent (${props.modLabel}])`,
  },
  { type: "divider" },
  {
    type: "button",
    command: "alignLeft",
    icon: IconAlignLeft,
    title: "Align left",
  },
  {
    type: "button",
    command: "alignCenter",
    icon: IconAlignCenter,
    title: "Align center",
  },
  {
    type: "button",
    command: "alignRight",
    icon: IconAlignRight,
    title: "Align right",
  },
  { type: "divider" },
  {
    type: "button",
    command: "link",
    icon: IconLink,
    title: `Link (${props.modLabel}K)`,
  },
  {
    type: "button",
    command: "image",
    icon: IconImage,
    title: `Image (${props.modLabel}⇧K)`,
  },
  {
    type: "button",
    command: "addComment",
    icon: IconComment,
    title: `Add comment (${props.modLabel}⇧M)`,
  },
  { type: "divider" },
  {
    type: "button",
    command: "heading1",
    icon: IconHeading1,
    title: `Heading 1 (${props.modLabel}${props.altLabel}1)`,
  },
  {
    type: "button",
    command: "heading2",
    icon: IconHeading2,
    title: `Heading 2 (${props.modLabel}${props.altLabel}2)`,
  },
  {
    type: "button",
    command: "heading3",
    icon: IconHeading3,
    title: `Heading 3 (${props.modLabel}${props.altLabel}3)`,
  },
]);

const bodyMirror = useTemplateRef<HTMLSpanElement>("bodyMirror");
const headingMirror = useTemplateRef<HTMLSpanElement>("headingMirror");
const bodyWidth = ref(0);
const headingWidth = ref(0);

const bodyLabel = computed(
  () =>
    EDITOR_FONTS.find((font) => font.id === props.bodyFontId)?.label ??
    props.bodyFontId,
);
const headingLabel = computed(
  () =>
    EDITOR_FONTS.find((font) => font.id === props.headingFontId)?.label ??
    props.headingFontId,
);

function measure(mirror: Maybe<HTMLSpanElement>): number {
  if (isNothing(mirror)) return 0;
  return Math.ceil(mirror.value.getBoundingClientRect().width + 8 + 22 + 2);
}

async function syncWidths(): Promise<void> {
  await nextTick();
  bodyWidth.value = measure(toMaybe(bodyMirror.value));
  headingWidth.value = measure(toMaybe(headingMirror.value));
}

onMounted(async () => {
  await syncWidths();
  void document.fonts?.ready.then(syncWidths);
});
watch(
  [bodyLabel, headingLabel, () => props.bodyFontId, () => props.headingFontId],
  syncWidths,
);

function onAction(command: FormatCommand): void {
  emit("command", command);
}

function onBodyFontChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value as EditorFontId;
  emit("update:bodyFontId", value);
}

function onHeadingFontChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value as EditorFontId;
  emit("update:headingFontId", value);
}
</script>

<template>
  <div class="relative flex items-center justify-center px-12 py-1.5">
    <div
      class="mx-auto flex w-fit max-w-full items-center gap-0.5 overflow-x-auto"
      role="toolbar"
      aria-label="Formatting"
      style="font-family: var(--font-ui)"
    >
      <template v-for="(item, index) in items" :key="index">
        <div
          v-if="item.type === 'divider'"
          class="mx-0.5 h-3.5 w-px shrink-0"
          style="background: var(--line)"
          aria-hidden="true"
        />
        <button
          v-else
          type="button"
          class="zw-ribbon-btn inline-flex size-7 shrink-0 items-center justify-center rounded-md"
          :title="item.title"
          :aria-label="item.title"
          @mousedown.prevent="onAction(item.command)"
        >
          <component :is="item.icon" class="size-4" aria-hidden="true" />
        </button>
      </template>

      <template v-if="showFontControls">
        <div
          class="mx-0.5 h-3.5 w-px shrink-0"
          style="background: var(--line)"
          aria-hidden="true"
        />

        <div class="flex shrink-0 items-center gap-2 pl-0.5">
          <div class="relative flex items-center gap-1">
            <span class="text-[11px]" style="color: var(--text-muted)"
              >Body</span
            >
            <label class="sr-only" for="editor-font-body">Body font</label>
            <span
              ref="bodyMirror"
              class="pointer-events-none invisible absolute left-0 top-0 whitespace-nowrap text-sm"
              :style="{ fontFamily: fontStackFor(bodyFontId) }"
              aria-hidden="true"
            >
              {{ bodyLabel }}
            </span>
            <select
              id="editor-font-body"
              class="editor-font-select zw-ribbon-btn cursor-pointer appearance-none rounded-md border-0 bg-transparent py-1 pl-1.5 pr-5 text-sm"
              :style="{
                fontFamily: fontStackFor(bodyFontId),
                width: bodyWidth > 0 ? `${bodyWidth}px` : 'auto',
              }"
              :value="bodyFontId"
              title="Body font"
              @mousedown.stop
              @change="onBodyFontChange"
            >
              <option
                v-for="font in EDITOR_FONTS"
                :key="font.id"
                :value="font.id"
                :style="{ fontFamily: font.stack }"
              >
                {{ font.label }}
              </option>
            </select>
          </div>

          <div class="relative flex items-center gap-1">
            <span class="text-[11px]" style="color: var(--text-muted)"
              >Headings</span
            >
            <label class="sr-only" for="editor-font-heading"
              >Heading font</label
            >
            <span
              ref="headingMirror"
              class="pointer-events-none invisible absolute left-0 top-0 whitespace-nowrap text-sm"
              :style="{ fontFamily: fontStackFor(headingFontId) }"
              aria-hidden="true"
            >
              {{ headingLabel }}
            </span>
            <select
              id="editor-font-heading"
              class="editor-font-select zw-ribbon-btn cursor-pointer appearance-none rounded-md border-0 bg-transparent py-1 pl-1.5 pr-5 text-sm"
              :style="{
                fontFamily: fontStackFor(headingFontId),
                width: headingWidth > 0 ? `${headingWidth}px` : 'auto',
              }"
              :value="headingFontId"
              title="Heading font"
              @mousedown.stop
              @change="onHeadingFontChange"
            >
              <option
                v-for="font in EDITOR_FONTS"
                :key="font.id"
                :value="font.id"
                :style="{ fontFamily: font.stack }"
              >
                {{ font.label }}
              </option>
            </select>
          </div>
        </div>
      </template>
    </div>

    <div class="absolute right-1 top-1/2 -translate-y-1/2">
      <slot name="trailing" />
    </div>
  </div>
</template>
